import type {
  ServiceGetAllResponse,
  ServiceGetByIdResponse,
  ServiceMutationOptionType,
  ServiceQueryOptionType
} from '@/types/service'

import { api, handleAxiosError } from '@/lib/axios'

import type {
  BulkVerificationResultType,
  VerificationDataType,
  VerificationRecapType,
  VerificationSettingType
} from './types'
import type {
  FilterSubmissionRecapValues,
  FilterSubmissionValues,
  MutationBulkVerificationValues,
  MutationVerificationDataValues
} from './validation'

const QUERY_KEY = (key?: string | Record<string, any>) =>
  ['verifikasi', key] as const

const getAll = (
  params?: FilterSubmissionValues
): ServiceQueryOptionType<
  ServiceGetAllResponse<Array<VerificationDataType>>
> => ({
  queryKey: QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetAllResponse<Array<VerificationDataType>>
      >(`/verifikasi`, {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getById = (
  id: string
): ServiceQueryOptionType<ServiceGetByIdResponse<VerificationDataType>> => ({
  queryKey: QUERY_KEY(id),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetByIdResponse<VerificationDataType>
      >(`/verifikasi/${id}`)

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const setApproval = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<VerificationDataType>,
  MutationVerificationDataValues
> => ({
  mutationFn: async (data: MutationVerificationDataValues) => {
    try {
      const response = await api.put<
        ServiceGetByIdResponse<VerificationDataType>
      >(`/verifikasi/${data.id}`, {
        ...data
      })
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const bulkUpdate = (): ServiceMutationOptionType<
  ServiceGetByIdResponse<BulkVerificationResultType>,
  MutationBulkVerificationValues
> => ({
  mutationFn: async (data: MutationBulkVerificationValues) => {
    try {
      const response = await api.post<
        ServiceGetByIdResponse<BulkVerificationResultType>
      >(`/verifikasi/bulk-update`, data)
      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getRecap = (
  params?: FilterSubmissionRecapValues
): ServiceQueryOptionType<ServiceGetByIdResponse<VerificationRecapType>> => ({
  queryKey: QUERY_KEY(params),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetByIdResponse<VerificationRecapType>
      >(`/verifikasi/rekap`, {
        params
      })

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

const getVerificationSetting = (): ServiceQueryOptionType<
  ServiceGetByIdResponse<VerificationSettingType>
> => ({
  queryKey: QUERY_KEY(),
  queryFn: async () => {
    try {
      const response = await api.get<
        ServiceGetByIdResponse<VerificationSettingType>
      >(`/setting/verification`)

      return response.data
    } catch (error) {
      throw handleAxiosError(error)
    }
  }
})

export const verifikasi = {
  queryKey: [QUERY_KEY(undefined)[0]],
  getAll,
  getRecap,
  getById,
  setApproval,
  bulkUpdate,
  getVerificationSetting
}
